// metal-weight.js
import React, { useState, useEffect } from "react";
import MainCalculatorsList from "./MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";
const TITLE = "Kalkulator Berat Logam - Hitung Berat Besi, Plat, Stainless, dan Aluminium";
const DESCRIPTION =
  "Kalkulator berat logam gratis - hitung berat besi, plat, stainless steel, aluminium, dan tembaga secara akurat. Masukkan dimensi dan dapatkan berat secara instan. Ideal untuk pipa, lembaran, batangan, dan siku.";
const KEYWORDS =
  "kalkulator berat besi, kalkulator berat plat, hitung berat logam, berat stainless, kalkulator pipa, hitung berat aluminium, berat batangan, kalkulator siku besi, steel weight calculator";
const OG_URL = "https://do-calculate.com/calculator/id/metal-weight/";

const SCHEMA = [
  {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Kalkulator Berat Logam",
    description: DESCRIPTION,
    url: OG_URL,
    applicationCategory: "UtilityApplication",
    operatingSystem: "Any",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "USD",
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Beranda",
        item: "https://do-calculate.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Kalkulator",
        item: "https://do-calculate.com/id/all-calculators/",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Kalkulator Berat Logam",
        item: OG_URL,
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "Bagaimana cara menghitung berat plat besi?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Untuk menghitung berat plat besi, masukkan panjang, lebar, dan ketebalan ke dalam kalkulator, lalu pilih jenis logam (besi, stainless, aluminium). Kalkulator akan menghitung berat secara otomatis berdasarkan massa jenis.",
        },
      },
      {
        "@type": "Question",
        name: "Berapa massa jenis besi?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Massa jenis besi karbon biasa adalah 7,85 g/cm³, sedangkan massa jenis stainless steel adalah 8,00 g/cm³, dan aluminium adalah 2,70 g/cm³.",
        },
      },
    ],
  },
];

const DENSITIES = {
  steel: { value: 7.85, name: "Besi Karbon" },
  stainless: { value: 8.0, name: "Stainless Steel" },
  aluminium: { value: 2.7, name: "Aluminium" },
  copper: { value: 8.96, name: "Tembaga" },
  brass: { value: 8.5, name: "Kuningan (Perunggu)" },
  zinc: { value: 7.14, name: "Seng" },
};

const PROFILE_TYPES = {
  plate: "Lembaran / Plat",
  round: "Batangan Bulat",
  pipe: "Pipa / Tabung",
  square: "Batangan Persegi",
  rectangular: "Batangan Persegi Panjang",
  angle: "Siku",
  square_tube: "Pipa Persegi",
  rectangular_tube: "Pipa Persegi Panjang",
  hex: "Batangan Segi Enam",
  ibeam: "Balok I",
};

function MetalWeightCalculator() {
  const [profileType, setProfileType] = useState("plate");
  const [material, setMaterial] = useState("steel");
  const [customDensity, setCustomDensity] = useState("");
  const [unit, setUnit] = useState("mm");
  const [quantity, setQuantity] = useState(1);
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [thickness, setThickness] = useState("");
  const [diameter, setDiameter] = useState("");
  const [outerDiameter, setOuterDiameter] = useState("");
  const [innerDiameter, setInnerDiameter] = useState("");
  const [height, setHeight] = useState("");
  const [leg1, setLeg1] = useState("");
  const [leg2, setLeg2] = useState("");
  const [flangeWidth, setFlangeWidth] = useState("");
  const [flangeThickness, setFlangeThickness] = useState("");
  const [webHeight, setWebHeight] = useState("");
  const [webThickness, setWebThickness] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const unitFactors = {
    mm: 1,
    cm: 10,
    m: 1000,
    inch: 25.4,
  };

  const toMm = (value) => {
    return parseFloat(value || 0) * unitFactors[unit];
  };

  const calculateWeight = () => {
    try {
      setError("");
      const density = material === "custom"
        ? parseFloat(customDensity || 0)
        : DENSITIES[material].value;
      if (density <= 0) {
        setError("Harap masukkan massa jenis yang valid");
        setResult(null);
        return;
      }
      let volumeCm3 = 0;

      switch (profileType) {
        case "plate":
          const plateLength = toMm(length) / 10;
          const plateWidth = toMm(width) / 10;
          const plateThickness = toMm(thickness) / 10;
          volumeCm3 = plateLength * plateWidth * plateThickness;
          break;
        case "round":
          const roundDiameter = toMm(diameter) / 10;
          const roundLength = toMm(length) / 10;
          const radius = roundDiameter / 2;
          volumeCm3 = Math.PI * radius * radius * roundLength;
          break;
        case "pipe":
          const pipeOuterD = toMm(outerDiameter) / 10;
          const pipeInnerD = toMm(innerDiameter) / 10;
          const pipeLength = toMm(length) / 10;
          const outerR = pipeOuterD / 2;
          const innerR = pipeInnerD / 2;
          volumeCm3 = Math.PI * (outerR * outerR - innerR * innerR) * pipeLength;
          break;
        case "square":
          const squareSide = toMm(width) / 10;
          const squareLength = toMm(length) / 10;
          volumeCm3 = squareSide * squareSide * squareLength;
          break;
        case "rectangular":
          const rectWidth = toMm(width) / 10;
          const rectHeight = toMm(height) / 10;
          const rectLength = toMm(length) / 10;
          volumeCm3 = rectWidth * rectHeight * rectLength;
          break;
        case "angle":
          const angleLeg1 = toMm(leg1) / 10;
          const angleLeg2 = toMm(leg2) / 10;
          const angleThickness = toMm(thickness) / 10;
          const angleLength = toMm(length) / 10;
          const crossSection = (angleLeg1 * angleThickness) +
            (angleLeg2 * angleThickness) -
            (angleThickness * angleThickness);
          volumeCm3 = crossSection * angleLength;
          break;
        case "square_tube":
          const sqTubeOuter = toMm(width) / 10;
          const sqTubeThickness = toMm(thickness) / 10;
          const sqTubeInner = sqTubeOuter - 2 * sqTubeThickness;
          const sqTubeLength = toMm(length) / 10;
          volumeCm3 = (sqTubeOuter * sqTubeOuter - sqTubeInner * sqTubeInner) * sqTubeLength;
          break;
        case "rectangular_tube":
          const rectTubeWidth = toMm(width) / 10;
          const rectTubeHeight = toMm(height) / 10;
          const rectTubeThickness = toMm(thickness) / 10;
          const rectTubeInnerWidth = rectTubeWidth - 2 * rectTubeThickness;
          const rectTubeInnerHeight = rectTubeHeight - 2 * rectTubeThickness;
          const rectTubeLength = toMm(length) / 10;
          volumeCm3 = (rectTubeWidth * rectTubeHeight -
            rectTubeInnerWidth * rectTubeInnerHeight) * rectTubeLength;
          break;
        case "hex":
          const hexSide = toMm(width) / 10;
          const hexLength = toMm(length) / 10;
          volumeCm3 = (Math.sqrt(3) / 2) * hexSide * hexSide * hexLength;
          break;
        case "ibeam":
          const iFlangeWidth = toMm(flangeWidth) / 10;
          const iFlangeThickness = toMm(flangeThickness) / 10;
          const iWebHeight = toMm(webHeight) / 10;
          const iWebThickness = toMm(webThickness) / 10;
          const iLength = toMm(length) / 10;
          const iCrossSection = (2 * iFlangeWidth * iFlangeThickness) +
            (iWebHeight * iWebThickness);
          volumeCm3 = iCrossSection * iLength;
          break;
        default:
          throw new Error("Jenis profil tidak dikenal");
      }

      const weightKg = (volumeCm3 * density) / 1000;
      const totalWeight = weightKg * quantity;
      const weightTon = totalWeight / 1000;
      const weightGram = totalWeight * 1000;
      const weightPerMeter = weightKg / (toMm(length) / 1000);

      setResult({
        weightPerPiece: weightKg,
        totalWeight,
        weightTon,
        weightGram,
        weightPerMeter,
        volumeCm3,
        density,
        materialName: material === "custom" ? "Kustom" : DENSITIES[material].name,
        profileName: PROFILE_TYPES[profileType],
      });
    } catch (err) {
      setError("Kesalahan perhitungan: " + err.message);
      setResult(null);
    }
  };

  useEffect(() => {
    if (length && (width || diameter || outerDiameter)) {
      calculateWeight();
    }
  }, [
    profileType,
    material,
    customDensity,
    unit,
    quantity,
    length,
    width,
    thickness,
    diameter,
    outerDiameter,
    innerDiameter,
    height,
    leg1,
    leg2,
    flangeWidth,
    flangeThickness,
    webHeight,
    webThickness,
  ]);

  const handleClear = () => {
    setLength("");
    setWidth("");
    setThickness("");
    setDiameter("");
    setOuterDiameter("");
    setInnerDiameter("");
    setHeight("");
    setLeg1("");
    setLeg2("");
    setFlangeWidth("");
    setFlangeThickness("");
    setWebHeight("");
    setWebThickness("");
    setQuantity(1);
    setResult(null);
    setError("");
  };

  const renderInputFields = () => {
    switch (profileType) {
      case "plate":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Lebar ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Lebar"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Ketebalan ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Ketebalan"
                />
              </div>
            </div>
          </>
        );
      case "round":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <label >Diameter ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={diameter}
                  onChange={(e) => setDiameter(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Diameter"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
            </div>
          </>
        );
      case "pipe":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >Diameter Luar ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={outerDiameter}
                  onChange={(e) => setOuterDiameter(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Diameter Luar"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Diameter Dalam ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={innerDiameter}
                  onChange={(e) => setInnerDiameter(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Diameter Dalam"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
            </div>
          </>
        );
      case "square":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <label >Panjang Sisi ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang Sisi"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
            </div>
          </>
        );
      case "rectangular":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >Lebar ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Lebar"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Tinggi ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Tinggi"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
            </div>
          </>
        );
      case "angle":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-3">
                <label >Sisi Pertama ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={leg1}
                  onChange={(e) => setLeg1(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Sisi Pertama"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >Sisi Kedua ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={leg2}
                  onChange={(e) => setLeg2(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Sisi Kedua"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >Ketebalan ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Ketebalan"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
            </div>
          </>
        );
      case "square_tube":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >Lebar Luar ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Lebar Luar"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Ketebalan Dinding ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Ketebalan Dinding"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
            </div>
          </>
        );
      case "rectangular_tube":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-3">
                <label >Lebar ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Lebar"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >Tinggi ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Tinggi"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >Ketebalan Dinding ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={thickness}
                  onChange={(e) => setThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Ketebalan Dinding"
                />
              </div>
              <div className="col-sm-12 col-md-3">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
            </div>
          </>
        );
      case "hex":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <label >Panjang Sisi ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang Sisi"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
            </div>
          </>
        );
      case "ibeam":
        return (
          <>
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <label >Lebar Sayap ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={flangeWidth}
                  onChange={(e) => setFlangeWidth(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Lebar Sayap"
                />
              </div>
              <div className="col-sm-12 col-md-6">
                <label >Ketebalan Sayap ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={flangeThickness}
                  onChange={(e) => setFlangeThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Ketebalan Sayap"
                />
              </div>
            </div>
            <div className="row">
              <div className="col-sm-12 col-md-4">
                <label >Tinggi Badan ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={webHeight}
                  onChange={(e) => setWebHeight(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Tinggi Badan"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Ketebalan Badan ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={webThickness}
                  onChange={(e) => setWebThickness(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Ketebalan Badan"
                />
              </div>
              <div className="col-sm-12 col-md-4">
                <label >Panjang ({unit})</label>
                <input
                  type="number"
                  className="form-control"
                  value={length}
                  onChange={(e) => setLength(e.target.value)}
                  min="0"
                  step="0.01"
                  placeholder="Panjang"
                />
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="ltr" >
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
        SCHEMA={SCHEMA}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>Kalkulator Berat Logam</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>Kalkulator Berat Logam</strong> adalah alat gratis dan akurat yang membantu Anda
                <strong>menghitung berat</strong> besi, plat, stainless steel, aluminium, dan tembaga dengan mudah. Masukkan dimensi potongan dan pilih jenis logam untuk mendapatkan berat yang akurat secara instan. Ideal untuk pipa, lembaran, batangan, siku, dan balok. Gunakan untuk penentuan harga, perhitungan pengiriman, atau perencanaan proyek teknik dan konstruksi.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />
            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  Hitung Berat Logam dengan Mudah
                </h2>
              </div>
              <div className="row">
                <div className="col-sm-12">
                  <label htmlFor="profileType" >
                    Jenis Profil
                  </label>
                  <select
                    id="profileType"
                    className="form-select"
                    value={profileType}
                    onChange={(e) => setProfileType(e.target.value)}
                  >
                    {Object.entries(PROFILE_TYPES).map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="material" >
                    Jenis Logam
                  </label>
                  <select
                    id="material"
                    className="form-select"
                    value={material}
                    onChange={(e) => setMaterial(e.target.value)}
                  >
                    {Object.entries(DENSITIES).map(([key, { name, value }]) => (
                      <option key={key} value={key}>
                        {name} ({value} g/cm³)
                      </option>
                    ))}
                    <option value="custom">Massa Jenis Kustom</option>
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="unit" >
                    Satuan Ukur
                  </label>
                  <select
                    id="unit"
                    className="form-select"
                    value={unit}
                    onChange={(e) => setUnit(e.target.value)}
                  >
                    <option value="mm">Milimeter (mm)</option>
                    <option value="cm">Sentimeter (cm)</option>
                    <option value="m">Meter (m)</option>
                    <option value="inch">Inci (inch)</option>
                  </select>
                </div>
              </div>
              {material === "custom" && (
                <div className="row">
                  <div className="col-sm-12">
                    <label htmlFor="customDensity" >
                      Massa Jenis Kustom (g/cm³)
                    </label>
                    <input
                      id="customDensity"
                      type="number"
                      className="form-control"
                      value={customDensity}
                      onChange={(e) => setCustomDensity(e.target.value)}
                      min="0"
                      step="0.01"
                      placeholder="Masukkan massa jenis"
                      style={{ backgroundColor: "#fff3cd" }}
                    />
                  </div>
                </div>
              )}
              {renderInputFields()}
              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="quantity" >
                    Kuantitas (Jumlah Potongan)
                  </label>
                  <input
                    id="quantity"
                    type="number"
                    className="form-control"
                    value={quantity}
                    onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                    min="1"
                    placeholder="Kuantitas"
                  />
                </div>
              </div>
              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleClear}
                  className="btn btn-secondary"
                  style={{
                    fontSize: "16px",
                    padding: "10px 30px",
                  }}
                >
                  Hapus
                </button>
              </div>
              {error && (
                <div className="alert alert-warning" role="alert">
                  <strong>Peringatan:</strong> {error}
                </div>
              )}
              {result && (
                <div className="mt-4">
                  <h3
                    style={{
                      fontSize: "18px",
                      marginBottom: "15px",
                      textAlign: "center",
                    }}
                  >
                    <strong>Hasil:</strong>
                  </h3>
                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            Keterangan
                          </th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>
                            Nilai
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>
                            Berat per Potongan
                           </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#1a5f3f",
                            }}
                          >
                            {result.weightPerPiece.toFixed(4)} kg
                           </td>
                         </tr>
                         <tr>
                          <td style={{ textAlign: "center" }}>
                            Berat Total ({quantity} Potongan)
                           </td>
                          <td
                            style={{
                              textAlign: "center",
                              fontSize: "18px",
                              fontWeight: "bold",
                              color: "#0f3d28",
                            }}
                          >
                            {result.totalWeight.toFixed(4)} kg
                           </td>
                         </tr>
                        {result.weightPerMeter > 0 && (
                           <tr>
                            <td style={{ textAlign: "center" }}>
                              Berat per Meter
                             </td>
                            <td style={{ textAlign: "center", fontSize: "16px" }}>
                              {result.weightPerMeter.toFixed(4)} kg/m
                             </td>
                           </tr>
                        )}
                      </tbody>
                     </table>
                  </div>
                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Format Berat Tambahan</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>
                            ⚖️ <strong>Dalam Ton:</strong>{" "}
                            {result.weightTon.toFixed(6)} ton
                          </li>
                          <li>
                            ⚖️ <strong>Dalam Gram:</strong>{" "}
                            {result.weightGram.toFixed(2)} g
                          </li>
                          <li>
                            📦 <strong>Volume:</strong>{" "}
                            {result.volumeCm3.toFixed(2)} cm³
                          </li>
                        </ul>
                        <div
                          className="alert alert-info mt-3"
                          style={{ fontSize: "14px" }}
                        >
                          <strong>💡 Informasi:</strong> Massa jenis yang digunakan:{" "}
                          {result.density} g/cm³ ({result.materialName})
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>Detail Perhitungan</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>Jenis Profil:</strong> {result.profileName}
                          </p>
                          <p>
                            <strong>Jenis Logam:</strong> {result.materialName}
                          </p>
                          <p>
                            <strong>Massa Jenis:</strong> {result.density} g/cm³
                          </p>
                          <p>
                            <strong>Volume:</strong> {result.volumeCm3.toFixed(2)} cm³
                          </p>
                          <hr />
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            <strong>Metode Perhitungan:</strong> Berat = Volume × Massa Jenis
                          </p>
                          <p style={{ fontSize: "14px", color: "#666" }}>
                            Volume = {result.volumeCm3.toFixed(2)} cm³
                            <br />
                            Massa Jenis = {result.density} g/cm³
                            <br />
                            Berat = {(result.volumeCm3 * result.density).toFixed(2)} g
                            = {result.weightPerPiece.toFixed(4)} kg
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>
      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <strong>Apa itu Kalkulator Berat Logam?</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Kalkulator Berat Logam</strong> adalah alat elektronik gratis yang membantu Anda menghitung berat setiap potongan logam secara akurat berdasarkan dimensi dan jenis logamnya. Kalkulator ini memungkinkan Anda untuk:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Menghitung berat berbagai jenis:</strong> Besi, stainless, aluminium, tembaga, kuningan, seng
                  </li>
                  <li>
                    <strong>Mendukung berbagai bentuk:</strong> Lembaran, pipa, batangan, siku, balok
                  </li>
                  <li>
                    <strong>Satuan ukur fleksibel:</strong> Milimeter, sentimeter, meter, inci
                  </li>
                  <li>
                    <strong>Perhitungan kuantitas:</strong> Hitung berat beberapa potongan sekaligus
                  </li>
                  <li>
                    <strong>Massa jenis kustom:</strong> Masukkan massa jenis khusus untuk logam lain
                  </li>
                </ul>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Cara Menggunakan Kalkulator:
                </h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih jenis profil (lembaran, pipa, batangan, dll.)</li>
                  <li>Pilih jenis logam dan satuan yang sesuai</li>
                  <li>Masukkan dimensi yang diperlukan sesuai jenis profil</li>
                  <li>Masukkan jumlah potongan jika ingin menghitung berat lebih dari satu potongan</li>
                  <li>Hasil akan muncul secara otomatis setelah data dimasukkan</li>
                </ol>
                <div
                  className="alert alert-info mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>💡 Catatan:</strong> Semua perhitungan menggunakan massa jenis standar setiap logam untuk memastikan akurasi hasil. Anda dapat menggunakan opsi "Massa Jenis Kustom" untuk logam yang tidak terdaftar atau untuk paduan khusus.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Penggunaan Kalkulator Berat Logam</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Dalam Konstruksi dan Bangunan:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Perhitungan kuantitas besi:</strong> Mengetahui berat besi beton untuk proyek
                  </li>
                  <li>
                    <strong>Estimasi biaya material:</strong> Menghitung biaya besi dan plat
                  </li>
                  <li>
                    <strong>Perencanaan logistik:</strong> Mengetahui berat muatan dan pengiriman
                  </li>
                  <li>
                    <strong>Balok dan struktur:</strong> Menghitung berat balok besi
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Di Bengkel dan Pabrik:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Penentuan harga produk:</strong> Menghitung biaya bahan baku
                  </li>
                  <li>
                    <strong>Manajemen inventaris:</strong> Mengetahui berat material yang disimpan
                  </li>
                  <li>
                    <strong>Permintaan pembelian:</strong> Menentukan kuantitas yang dibutuhkan secara akurat
                  </li>
                  <li>
                    <strong>Kontrol produksi:</strong> Menghitung konsumsi material
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Dalam Perdagangan dan Suplai:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Penentuan harga jual:</strong> Menghitung harga jual berdasarkan berat
                  </li>
                  <li>
                    <strong>Penawaran harga:</strong> Menyiapkan penawaran akurat untuk klien
                  </li>
                  <li>
                    <strong>Perhitungan pengiriman:</strong> Mengetahui biaya transportasi dan pengiriman
                  </li>
                  <li>
                    <strong>Perbandingan pemasok:</strong> Membandingkan harga berdasarkan berat
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Dalam Teknik dan Desain:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Desain struktural:</strong> Menghitung beban bangunan
                  </li>
                  <li>
                    <strong>Desain mesin:</strong> Mengetahui berat komponen
                  </li>
                  <li>
                    <strong>Pemilihan material:</strong> Membandingkan berbagai material
                  </li>
                  <li>
                    <strong>Studi teknis:</strong> Menyiapkan laporan teknis
                  </li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Massa Jenis Logam Umum</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Massa jenis adalah massa suatu zat per satuan volume, diukur dalam gram per sentimeter kubik (g/cm³). Berikut adalah massa jenis logam umum:
                </p>
                <table className="table bmi-table" style={{ marginTop: "1rem" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        Logam
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        Massa Jenis (g/cm³)
                      </th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>
                        Penggunaan
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ textAlign: "center" }}>Besi Karbon</td>
                      <td style={{ textAlign: "center" }}>7,85</td>
                      <td style={{ textAlign: "center" }}>Konstruksi, besi beton</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Stainless Steel</td>
                      <td style={{ textAlign: "center" }}>8,00</td>
                      <td style={{ textAlign: "center" }}>Dapur, alat medis</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Aluminium</td>
                      <td style={{ textAlign: "center" }}>2,70</td>
                      <td style={{ textAlign: "center" }}>Jendela, pesawat</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Tembaga</td>
                      <td style={{ textAlign: "center" }}>8,96</td>
                      <td style={{ textAlign: "center" }}>Kabel listrik</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Kuningan (Perunggu)</td>
                      <td style={{ textAlign: "center" }}>8,50</td>
                      <td style={{ textAlign: "center" }}>Keran, alat</td>
                    </tr>
                    <tr>
                      <td style={{ textAlign: "center" }}>Seng</td>
                      <td style={{ textAlign: "center" }}>7,14</td>
                      <td style={{ textAlign: "center" }}>Galvanisasi, baterai</td>
                    </tr>
                  </tbody>
                </table>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>
                  Mengapa Massa Jenis Berbeda?
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  Massa jenis logam bergantung pada:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Struktur atom logam</li>
                  <li>Cara susunan atom dalam struktur kristal</li>
                  <li>Adanya kotoran atau unsur paduan</li>
                  <li>Suhu dan tekanan</li>
                </ul>
                <div
                  className="alert alert-success mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>✓ Manfaat:</strong> Aluminium jauh lebih ringan daripada besi (sekitar sepertiga beratnya), oleh karena itu digunakan dalam aplikasi yang membutuhkan bobot ringan seperti pesawat dan mobil.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Contoh Praktis Perhitungan Berat</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Contoh 1: Menghitung Berat Lembaran Plat
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Masalah:</strong> Anda memiliki lembaran plat besi dengan ukuran 2 meter × 1 meter × 3 mm. Berapa beratnya?
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Solusi:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Lembaran / Plat" sebagai jenis profil</li>
                  <li>Pilih "Besi Karbon" sebagai jenis logam</li>
                  <li>Masukkan: panjang = 2000 mm, lebar = 1000 mm, ketebalan = 3 mm</li>
                  <li>Hasil: sekitar 47,1 kg</li>
                </ol>
                <div className="alert alert-info" style={{ fontSize: "14px" }}>
                  <strong>Perhitungan:</strong> Volume = 200 × 100 × 0,3 = 6000 cm³
                  <br />
                  Berat = 6000 × 7,85 = 47.100 g = 47,1 kg
                </div>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 2: Menghitung Berat Pipa
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Masalah:</strong> Pipa stainless dengan diameter luar 50 mm dan diameter dalam 44 mm serta panjang 6 meter. Berapa beratnya?
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Solusi:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Pipa / Tabung"</li>
                  <li>Pilih "Stainless Steel"</li>
                  <li>Masukkan: diameter luar = 50 mm, diameter dalam = 44 mm, panjang = 6000 mm</li>
                  <li>Hasil: sekitar 11,07 kg</li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 3: Menghitung Berat Batangan Bulat
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Masalah:</strong> Batangan aluminium bulat dengan diameter 20 mm dan panjang 3 meter. Berapa beratnya?
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Solusi:</strong>
                </p>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>Pilih "Batangan Bulat"</li>
                  <li>Pilih "Aluminium"</li>
                  <li>Masukkan: diameter = 20 mm, panjang = 3000 mm</li>
                  <li>Hasil: sekitar 2,54 kg</li>
                </ol>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Contoh 4: Menghitung Berat Siku Besi
                </h3>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Masalah:</strong> Siku besi 50×50 mm dengan ketebalan 5 mm dan panjang 6 meter. Berapa beratnya?
                </p>
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Solusi:</strong> Pilih "Siku", dan masukkan dimensi, hasilnya: sekitar 22,81 kg
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Tips Menggunakan Kalkulator Berat Secara Efektif</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Tips Umum:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Gunakan satuan yang sama:</strong> Pastikan semua dimensi dimasukkan dengan satuan yang sama untuk menghindari kesalahan
                  </li>
                  <li>
                    <strong>Ukur dengan akurat:</strong> Gunakan alat ukur yang akurat untuk mendapatkan hasil yang benar
                  </li>
                  <li>
                    <strong>Periksa hasil:</strong> Periksa hasil di bagian detail
                  </li>
                  <li>
                    <strong>Simpan perhitungan:</strong> Ambil screenshot atau tulis hasilnya untuk referensi
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Untuk Menghindari Kesalahan:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Pastikan memilih jenis profil yang benar</li>
                  <li>Pilih jenis logam yang sesuai</li>
                  <li>Masukkan diameter dalam lebih kecil dari diameter luar untuk pipa</li>
                  <li>Periksa satuan ukur sebelum memasukkan data</li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Untuk Penggunaan Profesional:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Hitung berat satu sampel terlebih dahulu lalu kalikan dengan kuantitas</li>
                  <li>Tambahkan margin 3-5% untuk toleransi manufaktur</li>
                  <li>Simpan tabel berat umum untuk referensi cepat</li>
                  <li>Gunakan berat per meter untuk perencanaan keuangan</li>
                </ul>
                <div
                  className="alert alert-warning mt-3"
                  style={{ lineHeight: "1.8" }}
                >
                  <strong>⚠️ Peringatan:</strong> Berat yang dihitung adalah berat teoritis berdasarkan massa jenis standar. Berat sebenarnya mungkin sedikit berbeda karena toleransi manufaktur atau kotoran.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <strong>Fakta Menarik tentang Logam</strong>
              </div>
              <div className="info-section-body">
                <h3 style={{ fontSize: "17px", marginBottom: "10px" }}>
                  Fakta Menarik:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Besi dalam tubuh Anda:</strong> Tubuh manusia mengandung sekitar 4 gram besi, sebagian besar dalam darah!
                  </li>
                  <li>
                    <strong>Menara Eiffel:</strong> Beratnya sekitar 10.000 ton besi, dan dicat ulang setiap 7 tahun
                  </li>
                  <li>
                    <strong>Aluminium:</strong> Lebih mahal dari emas pada abad ke-19 karena sulitnya ekstraksi
                  </li>
                  <li>
                    <strong>Tembaga:</strong> Logam pertama yang digunakan manusia lebih dari 10.000 tahun yang lalu
                  </li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Angka Menarik:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Produksi besi dunia: sekitar 1,8 miliar ton per tahun</li>
                  <li>Setiap 40 detik, satu ton aluminium diproduksi di dunia</li>
                  <li>Stainless steel tidak berkarat karena mengandung setidaknya 10% kromium</li>
                  <li>Aluminium dapat didaur ulang tanpa batas tanpa kehilangan kualitasnya</li>
                </ul>
                <h3
                  style={{
                    fontSize: "17px",
                    marginTop: "15px",
                    marginBottom: "10px",
                  }}
                >
                  Di Era Modern:
                </h3>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>
                    <strong>Pencakar langit:</strong> Menggunakan ribuan ton besi dan baja
                  </li>
                  <li>
                    <strong>Mobil:</strong> Mobil modern mengandung sekitar 900 kg besi
                  </li>
                  <li>
                    <strong>Pesawat:</strong> 80% dari struktur pesawat terbuat dari aluminium
                  </li>
                  <li>
                    <strong>Kabel:</strong> Lebih dari 60% produksi tembaga digunakan untuk kabel
                  </li>
                </ul>
              </div>
            </div>
          </div>
          <br />
          <h2 style={{ fontSize: "20px", marginBottom: "15px" }}>
            Pertanyaan Umum
          </h2>
          <div className="info-sections-container">
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bagaimana cara menghitung berat plat besi?</h3>
              </div>
              <div className="info-section-body">
                Untuk menghitung berat plat besi:
                <ol style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>Pilih "Lembaran / Plat" dari daftar jenis profil</li>
                  <li>Pilih jenis logam (besi, stainless, aluminium)</li>
                  <li>Pilih satuan ukur yang sesuai (mm, cm, meter)</li>
                  <li>Masukkan panjang, lebar, dan ketebalan</li>
                  <li>Hasil akan muncul secara otomatis dalam kilogram</li>
                </ol>
                <p style={{ marginTop: "10px" }}>
                  <strong>Contoh:</strong> Plat besi 2m × 1m × 3mm = sekitar 47,1 kg
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Berapa massa jenis besi?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Massa jenis besi karbon biasa adalah <strong>7,85 g/cm³</strong>.
                  Ini berarti setiap sentimeter kubik besi memiliki berat 7,85 gram.
                </p>
                <p style={{ lineHeight: "1.8", marginTop: "10px" }}>
                  <strong>Massa jenis logam lainnya:</strong>
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Stainless steel: 8,00 g/cm³</li>
                  <li>Aluminium: 2,70 g/cm³</li>
                  <li>Tembaga: 8,96 g/cm³</li>
                  <li>Kuningan: 8,50 g/cm³</li>
                </ul>
                <p style={{ marginTop: "10px" }}>
                  Nilai-nilai ini digunakan dalam kalkulator untuk memastikan akurasi hasil.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>Bagaimana cara menghitung berat pipa besi?</h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Untuk menghitung berat pipa (tabung berongga):
                </p>
                <ol style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>Pilih "Pipa / Tabung" dari daftar profil</li>
                  <li>Pilih jenis logam</li>
                  <li>Masukkan diameter luar pipa</li>
                  <li>Masukkan diameter dalam (atau hitung: luar - 2×ketebalan)</li>
                  <li>Masukkan panjang</li>
                  <li>Kalkulator akan menghitung volume logam aktual (tanpa ruang kosong di dalam)</li>
                </ol>
                <div className="alert alert-info mt-2" style={{ fontSize: "14px" }}>
                  <strong>Contoh:</strong> Pipa besi diameter luar 50 mm, diameter dalam 44 mm (ketebalan 3 mm), panjang 6 meter = sekitar 10,99 kg
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Mengapa berat aluminium berbeda dengan besi?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Perbedaan berat disebabkan oleh perbedaan <strong>massa jenis</strong>:
                </p>
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>Besi:</strong> Massa jenis 7,85 g/cm³
                  </li>
                  <li>
                    <strong>Aluminium:</strong> Massa jenis 2,70 g/cm³
                  </li>
                </ul>
                <p style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  Ini berarti aluminium <strong>sekitar 3 kali lebih ringan</strong> dari besi! Potongan aluminium dengan ukuran yang sama dengan potongan besi akan memiliki berat hanya sekitar sepertiganya.
                </p>
                <div
                  className="alert alert-success mt-2"
                  style={{ fontSize: "14px" }}
                >
                  <strong>Manfaat:</strong> Karena itulah aluminium digunakan dalam pesawat dan mobil di mana bobot ringan sangat penting, meskipun besi lebih kuat dan lebih murah.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Bisakah saya menghitung berat lebih dari satu potongan?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Ya!</strong> Kalkulator mendukung perhitungan berat beberapa potongan:
                </p>
                <ol style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>Masukkan dimensi satu potongan seperti biasa</li>
                  <li>Di kolom "Kuantitas (Jumlah Potongan)", masukkan jumlah yang diinginkan</li>
                  <li>
                    Hasil akan menampilkan:
                    <ul style={{ marginTop: "5px" }}>
                      <li>Berat per potongan</li>
                      <li>Berat total untuk semua potongan</li>
                    </ul>
                  </li>
                </ol>
                <p style={{ marginTop: "10px" }}>
                  <strong>Contoh:</strong> Jika Anda ingin menghitung berat 50 lembar plat, masukkan dimensi satu lembar lalu tulis 50 di kolom kuantitas.
                </p>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Bagaimana jika logam saya tidak ada dalam daftar?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Jika logam yang Anda inginkan tidak ada dalam daftar, Anda dapat menggunakan opsi <strong>"Massa Jenis Kustom"</strong>:
                </p>
                <ol style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>Pilih "Massa Jenis Kustom" dari daftar jenis logam</li>
                  <li>Kolom baru akan muncul untuk memasukkan massa jenis</li>
                  <li>Masukkan massa jenis logam dalam gram per sentimeter kubik (g/cm³)</li>
                  <li>Lanjutkan memasukkan dimensi seperti biasa</li>
                </ol>
                <p style={{ marginTop: "10px" }}>
                  <strong>Di mana saya bisa menemukan massa jenis logam?</strong>
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Cari di Google atau referensi material teknik</li>
                  <li>Tanyakan kepada pemasok atau pabrikan</li>
                  <li>Lihat spesifikasi teknis produk</li>
                </ul>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Apakah hasilnya 100% akurat?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  Kalkulator memberikan <strong>hasil teoritis yang sangat akurat</strong> berdasarkan:
                </p>
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>Dimensi yang dimasukkan</li>
                  <li>Massa jenis standar logam</li>
                  <li>Rumus perhitungan volume yang benar</li>
                </ul>
                <p style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <strong>Namun mungkin ada sedikit perbedaan</strong> (biasanya kurang dari 2-3%) antara berat yang dihitung dan berat sebenarnya karena:
                </p>
                <ul style={{ lineHeight: "1.8" }}>
                  <li>Toleransi manufaktur dalam dimensi</li>
                  <li>Perbedaan kecil dalam massa jenis tergantung komposisi kimia</li>
                  <li>Adanya kotoran atau unsur paduan</li>
                  <li>Perlakuan panas</li>
                </ul>
                <div
                  className="alert alert-warning mt-2"
                  style={{ fontSize: "14px" }}
                >
                  <strong>Tips:</strong> Untuk bisnis, tambahkan margin keamanan 3-5% pada berat yang dihitung untuk menutupi perbedaan ini.
                </div>
              </div>
            </div>
            <div className="info-section">
              <div className="info-section-header">
                <h3 style={{ fontSize: "16px" }}>
                  Bisakah saya menggunakan kalkulator di ponsel?
                </h3>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>Tentu saja!</strong> Kalkulator dirancang untuk bekerja secara efisien di semua perangkat:
                </p>
                <ul style={{ marginTop: "10px", lineHeight: "1.8" }}>
                  <li>
                    <strong>📱 Smartphone:</strong> Antarmuka responsif dan nyaman untuk layar kecil
                  </li>
                  <li>
                    <strong>📲 Tablet:</strong> Pengalaman ideal dengan ruang yang lebih luas
                  </li>
                  <li>
                    <strong>💻 Komputer:</strong> Antarmuka lengkap dengan semua fitur
                  </li>
                </ul>
                <p style={{ marginTop: "10px" }}>
                  Kalkulator bekerja di semua browser modern (Chrome, Safari, Firefox, Edge) dan menghitung hasil secara instan tanpa memerlukan aplikasi tambahan.
                </p>
                <div
                  className="alert alert-info mt-2"
                  style={{ fontSize: "14px" }}
                >
                  <strong>💡 Tips:</strong> Anda dapat menggunakan kalkulator langsung dari ponsel Anda saat berada di bengkel atau pabrik!
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MetalWeightCalculator;